
import { Card } from '@/components/ui/card';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';

interface ChartConfig {
  id: string;
  type: 'bar' | 'line' | 'pie' | 'area';
  title: string;
  data: any[];
  xAxis?: string;
  yAxis?: string;
  color?: string;
}

interface DynamicChartProps {
  config: ChartConfig;
  data: any[];
}

const COLORS = [
  'hsl(210, 70%, 50%)',
  'hsl(160, 70%, 50%)',
  'hsl(30, 70%, 50%)',
  'hsl(280, 70%, 50%)',
  'hsl(120, 70%, 50%)',
  'hsl(350, 70%, 50%)',
  'hsl(60, 70%, 50%)',
  'hsl(240, 70%, 50%)',
];

export const DynamicChart = ({ config, data }: DynamicChartProps) => {
  const renderChart = () => {
    const chartData = data.length > 0 ? data : config.data;
    
    switch (config.type) {
      case 'bar':
        return (
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey={config.xAxis || 'name'} 
              tick={{ fontSize: 12 }}
              interval={0}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Bar 
              dataKey={config.yAxis || 'value'} 
              fill={config.color || 'hsl(210, 70%, 50%)'} 
            />
          </BarChart>
        );

      case 'line':
        return (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey={config.xAxis || 'name'} 
              tick={{ fontSize: 12 }}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Line 
              type="monotone" 
              dataKey={config.yAxis || 'value'} 
              stroke={config.color || 'hsl(210, 70%, 50%)'} 
              strokeWidth={2}
            />
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey={config.xAxis || 'x'} 
              tick={{ fontSize: 12 }}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Area 
              type="monotone" 
              dataKey={config.yAxis || 'y'} 
              stroke={config.color || 'hsl(210, 70%, 50%)'} 
              fill={config.color || 'hsl(210, 70%, 50%)'} 
              fillOpacity={0.3}
            />
          </AreaChart>
        );

      case 'pie':
        return (
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        );

      default:
        return <div className="text-center text-muted-foreground">Unsupported chart type</div>;
    }
  };

  return (
    <Card className="p-6">
      <div className="mb-4">
        <h3 className="text-lg font-medium">{config.title}</h3>
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>
    </Card>
  );
};
